//frontend/src/components/common/RouteSelector.jsx

import React, { useState, useRef, useEffect, useMemo } from 'react'
import IconChevronDown from '../icons/IconChevronDown'
import IconCheck from '../icons/IconCheck'
import { dedupeBy, compareAlphanumeric, cycleIndex } from '@core/domain/collections'

export default function RouteSelector({ routes, selected, onChange }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState(selected ?? '')
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const ref = useRef(null)
  const inputRef = useRef(null)
  const listRef = useRef(null)

  // ---- Deduplicate routes by id ----
  const uniqueRoutes = useMemo(
    () => dedupeBy(routes, r => r.id),
    [routes]
  )

  // Filter and sort the deduplicated list
  const filtered = useMemo(
    () => uniqueRoutes
      .filter(r => r.number.toLowerCase().includes(query.trim().toLowerCase()))
      .sort((a, b) => compareAlphanumeric(a.number, b.number)),
    [uniqueRoutes, query]
  )

  // Clamp highlight when the filtered list shrinks
  useEffect(() => {
    if (highlightedIndex >= filtered.length) {
      setHighlightedIndex(Math.max(0, filtered.length - 1))
    }
  }, [filtered.length, highlightedIndex])

  // Reset highlight to the selected route when the dropdown opens
  useEffect(() => {
    if (!open) return
    const selectedIdx = filtered.findIndex(r => r.id === selected)
    setHighlightedIndex(selectedIdx >= 0 ? selectedIdx : 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Scroll the highlighted item into view
  useEffect(() => {
    if (!open || !listRef.current || filtered.length === 0) return
    const item = listRef.current.children[highlightedIndex]
    if (item && typeof item.scrollIntoView === 'function') {
      item.scrollIntoView({ block: 'nearest' })
    }
  }, [highlightedIndex, open, filtered.length])

  // ---- Click outside to close ----
  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false)
        const selectedRoute = uniqueRoutes.find(r => r.id === selected)
        setQuery(selectedRoute?.number ?? selected ?? '')
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [selected, uniqueRoutes])

  // ---- Selection ----
  const selectRoute = (route) => {
    onChange(route.id)
    setQuery(route.number)
    setOpen(false)
  }

  const closeWithoutSelecting = () => {
    setOpen(false)
    const selectedRoute = uniqueRoutes.find(r => r.id === selected)
    setQuery(selectedRoute?.number ?? selected ?? '')
  }

  // ---- Keyboard handling ----
  const handleKeyDown = (e) => {
    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault()
        if (!open) {
          setOpen(true)
        } else {
          setHighlightedIndex(i => cycleIndex(i, filtered.length, 1))
        }
        break
      }
      case 'ArrowUp': {
        e.preventDefault()
        if (!open) {
          setOpen(true)
        } else {
          setHighlightedIndex(i => cycleIndex(i, filtered.length, -1))
        }
        break
      }
      case 'Enter': {
        e.preventDefault()
        if (open) {
          const route = filtered[highlightedIndex]
          if (route) selectRoute(route)
        }
        break
      }
      case 'Escape': {
        if (open) {
          e.preventDefault()
          closeWithoutSelecting()
        }
        break
      }
      default:
        break
    }
  }

  return (
    <div
      ref={ref}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: 'var(--route-selector-width)',
        flexShrink: 0,
      }}
    >
      <label
        style={{
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '0.07em',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
          whiteSpace: 'nowrap',
        }}
      >
        Select Route
      </label>

      <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
        <div
          onClick={() => { setOpen(o => !o); inputRef.current?.focus() }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            padding: '0 10px',
            height: 34,
            background: 'var(--bg-elevated)',
            border: '1px solid',
            borderColor: open ? 'var(--accent)' : 'var(--border)',
            borderRadius: 8,
            cursor: 'text',
            transition: 'border-color 0.15s, box-shadow 0.15s',
            boxShadow: open ? '0 0 0 3px var(--accent-dim)' : 'none',
          }}
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--text-muted)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ flexShrink: 0 }}
          >
            <rect x="1" y="4" width="22" height="14" rx="2" />
            <path d="M1 10h22" />
            <path d="M7 10v4M17 10v4" />
          </svg>

          <input
            ref={inputRef}
            value={query}
            onChange={e => {
              setQuery(e.target.value)
              setOpen(true)
              setHighlightedIndex(0)
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder="Route…"
            style={{
              background: 'none',
              border: 'none',
              outline: 'none',
              flex: 1,
              minWidth: 0,
              fontSize: 13,
              fontWeight: 700,
              fontFamily: "'JetBrains Mono', monospace",
              caretColor: 'var(--accent)',
              color: 'var(--text-primary)',
            }}
          />

          <span style={{ display: 'flex', flexShrink: 0 }}>
            <IconChevronDown open={open} />
          </span>
        </div>

        {open && (
          <ul
            ref={listRef}
            style={{
              position: 'absolute',
              top: 'calc(100% + 5px)',
              left: 0,
              right: 0,
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 9,
              boxShadow: 'var(--shadow-lg)',
              listStyle: 'none',
              margin: 0,
              padding: '4px 0',
              maxHeight: 230,
              overflowY: 'auto',
              zIndex: 3000,
            }}
          >
            {filtered.length === 0 ? (
              <li
                style={{
                  padding: '8px 12px',
                  fontSize: 12,
                  color: 'var(--text-muted)',
                  fontStyle: 'italic',
                }}
              >
                No matching routes
              </li>
            ) : (
              filtered.map((r, idx) => {
                const isSelected = r.id === selected
                const isHighlighted = idx === highlightedIndex
                let bg = 'transparent'
                if (isSelected) bg = 'var(--accent-dim)'
                else if (isHighlighted) bg = 'var(--bg-elevated)'
                return (
                  <li
                    key={r.id}
                    onClick={() => selectRoute(r)}
                    onMouseEnter={() => setHighlightedIndex(idx)}
                    style={{
                      padding: '7px 12px',
                      fontSize: 13,
                      fontWeight: 700,
                      fontFamily: "'JetBrains Mono', monospace",
                      color: isSelected ? 'var(--accent)' : 'var(--text-primary)',
                      background: bg,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      transition: 'background 0.1s',
                    }}
                  >
                    {r.number}
                    {isSelected && <IconCheck />}
                  </li>
                )
              })
            )}
          </ul>
        )}
      </div>
    </div>
  )
}